import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ContentCopy as ContentCopyIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import DeviceService from "../../../api/deviceService";
import CustomConfirmationDialog from "../../dialogs/CustomConfirmationDialog";
import { dispatchSnackBarMessage } from "../../../utils/snackbar";

const DeviceDetailsDialog = ({ open, onClose, device, onTokenRegenerated }) => {
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [regenerateDialogOpen, setRegenerateDialogOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState(device);

  React.useEffect(() => {
    setCurrentDevice(device);
  }, [device]);

  const handleClose = () => {
    if (loading) {
      return;
    }

    setCopied(false);
    onClose();
  };

  const copyToClipboard = () => {
    if (currentDevice?.token) {
      navigator.clipboard
        .writeText(currentDevice.token)
        .then(() => {
          setCopied(true);
          dispatchSnackBarMessage(
            Date.now(),
            "Token copiado para a área de transferência",
            "success"
          );

          setTimeout(() => setCopied(false), 3000);
        })
        .catch((err) => {
          console.error("Error copying to clipboard:", err);
          dispatchSnackBarMessage(
            Date.now(),
            "Erro ao copiar o token",
            "error"
          );
        });
    }
  };

  const handleRegenerateToken = async () => {
    if (!currentDevice) {
      return;
    }

    try {
      setLoading(true);

      const response = await DeviceService.regenerateToken(currentDevice.id);

      if (response?.data) {
        setCurrentDevice(response.data);
        onTokenRegenerated(response.data);
        setRegenerateDialogOpen(false);

        dispatchSnackBarMessage(
          Date.now(),
          "Token regenerado com sucesso",
          "success"
        );
      }
    } catch (err) {
      console.error("Error regenerating token:", err);
      dispatchSnackBarMessage(Date.now(), "Erro ao regenerar token", "error");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return "Nunca usado";
    }

    return new Date(dateString).toLocaleString("pt-BR");
  };

  if (!currentDevice) {
    return null;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 999999 }}
      >
        <DialogTitle>Detalhes do Dispositivo</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Nome
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {currentDevice.name}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Identificador do Dispositivo
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {currentDevice.device_identifier}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  {currentDevice.is_active ? (
                    <Chip label="Ativo" color="success" size="small" />
                  ) : (
                    <Chip label="Revogado" color="error" size="small" />
                  )}
                </Box>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Proprietário
                </Typography>
                <Typography variant="body1">
                  {currentDevice.owner_username}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Registrado por
                </Typography>
                <Typography variant="body1">
                  {currentDevice.registered_by_username || "-"}
                </Typography>
              </Grid>

              <Grid item xs={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Data de Criação
                </Typography>
                <Typography variant="body1">
                  {formatDate(currentDevice.created_at)}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Último Uso
                </Typography>
                <Typography variant="body1">
                  {formatDate(currentDevice.last_used_at)}
                </Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" color="textSecondary">
                  Token de Autenticação
                </Typography>
                <Tooltip title="Regenerar Token">
                  <IconButton
                    size="small"
                    onClick={() => setRegenerateDialogOpen(true)}
                    disabled={loading || !currentDevice.is_active}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center" }}>
                <TextField
                  fullWidth
                  value={currentDevice.token || ""}
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                  size="small"
                />
                <Tooltip title={copied ? "Copiado!" : "Copiar Token"}>
                  <IconButton
                    onClick={copyToClipboard}
                    sx={{ ml: 1 }}
                    color={copied ? "success" : "default"}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              <Alert severity="warning" sx={{ mt: 2 }}>
                Este token é usado para autenticar o dispositivo. Não
                compartilhe este token com ninguém. Se você suspeitar que o
                token foi comprometido, regenere-o imediatamente.
              </Alert>

              {!currentDevice.is_active && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Este dispositivo foi revogado e não pode mais se conectar.
                  Regenere o token para reativar o dispositivo.
                </Alert>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <CustomConfirmationDialog
        open={regenerateDialogOpen}
        onClose={() => setRegenerateDialogOpen(false)}
        onConfirm={handleRegenerateToken}
        title="Regenerar Token"
        dialogContent={`Tem certeza que deseja regenerar o token do dispositivo "${currentDevice.name}"? O token atual será invalidado e um novo token será gerado. O dispositivo precisará ser reconfigurado com o novo token.`}
        confirmText="Regenerar"
        cancelText="Cancelar"
      />
    </>
  );
};

export default DeviceDetailsDialog;
